import {
  NAV_GROUP_LABELS,
  NAV_GROUP_ORDER,
  navItems,
  type NavGroup
} from '@/config/nav-config';

/**
 * The four verbs that appear across nearly every permission group
 * (view/edit/delete/create cover ~106 of the 118 action permissions).
 * Everything else is a one-off that belongs to a single group, so it is
 * only ever rendered inside that group — never as a global control.
 */
export const CORE_ACTIONS = ['view', 'create', 'edit', 'delete'] as const;

/** Maps backend permission group keys to translation keys */
export const GROUP_KEY_MAP: Record<string, string> = {
  dashboard: 'group_dashboard',
  supervisor: 'group_supervisor',
  roles: 'group_roles',
  users: 'group_users',
  container_types: 'group_container_types',
  sub_container_types: 'group_sub_container_types',
  storage_locations: 'group_storage_locations',
  locations: 'group_locations',
  system_logs: 'group_system_logs',
  registrations: 'group_registrations',
  page_indexes: 'group_page_indexes',
  print_batches: 'group_print_batches',
  box_types: 'group_box_types',
  boxes: 'group_boxes',
  box_containers: 'group_box_containers',
  dynamic_forms: 'group_dynamic_forms',
  photo_scanning: 'group_photo_scanning',
  review: 'group_review',
  reception: 'group_reception',
  devices: 'group_devices',
  shipments: 'group_shipments',
  handover_scanning: 'group_handover_scanning',
  sorting: 'group_sorting',
  shelves: 'group_shelves',
  clients: 'group_clients',
  qr_review: 'group_qr_review',
  repairs: 'group_repairs',
  warehouse: 'group_warehouse',
  configurations: 'group_configurations',
  ai_models: 'group_ai_models',
  rejection_reasons: 'group_rejection_reasons',
  labs: 'group_labs',
  ocr: 'group_ocr',
  ocr_templates: 'group_ocr_templates',
  repair_settings: 'group_repair_settings',
  qa1_ocr: 'group_qa1_ocr',
  qa2_ocr: 'group_qa2_ocr',
  qa3_ocr: 'group_qa3_ocr',
  localization: 'group_localization',
  search: 'group_search',
  branding: 'group_branding',
  telescope: 'group_telescope',
  dev_tools: 'group_dev_tools'
};

/** Maps backend permission action keys to translation keys */
export const ACTION_KEY_MAP: Record<string, string> = {
  view: 'action_view',
  create: 'action_create',
  edit: 'action_edit',
  delete: 'action_delete',
  force_delete: 'action_force_delete',
  restore: 'action_restore',
  export: 'action_export',
  import: 'action_import',
  update: 'action_update',
  operate: 'action_operate',
  view_filter: 'action_view_filter',
  rollback: 'action_rollback',
  recycle: 'action_recycle',
  sync: 'action_sync',
  receive_rejection_alerts: 'action_receive_rejection_alerts',
  self_assign: 'action_self_assign',
  cross_client: 'action_cross_client',
  view_logo: 'action_view_logo',
  use: 'action_use',
  release: 'action_release',
  transfer: 'action_transfer',
  assign: 'action_assign',
  list: 'action_list'
};

/** Maps backend permission group keys to Iconify icon names */
export const GROUP_ICON_MAP: Record<string, string> = {
  dashboard: 'solar:home-bold-duotone',
  supervisor: 'solar:user-speak-bold-duotone',
  users: 'solar:users-group-two-rounded-bold-duotone',
  roles: 'solar:shield-user-bold-duotone',
  system_logs: 'solar:monitor-bold-duotone',
  registrations: 'solar:document-add-bold-duotone',
  container_types: 'solar:box-bold-duotone',
  sub_container_types: 'solar:archive-minimalistic-bold-duotone',
  storage_locations: 'solar:map-point-bold-duotone',
  locations: 'solar:map-bold-duotone',
  page_indexes: 'solar:book-2-bold-duotone',
  print_batches: 'solar:printer-bold-duotone',
  box_types: 'solar:inbox-archive-bold-duotone',
  boxes: 'solar:box-minimalistic-bold-duotone',
  box_containers: 'solar:checklist-minimalistic-bold-duotone',
  dynamic_forms: 'solar:file-text-bold-duotone',
  photo_scanning: 'solar:gallery-send-bold-duotone',
  review: 'solar:eye-bold-duotone',
  reception: 'solar:incoming-call-bold-duotone',
  devices: 'solar:devices-bold-duotone',
  shipments: 'solar:delivery-bold-duotone',
  handover_scanning: 'solar:scanner-bold-duotone',
  sorting: 'solar:sort-by-alphabet-bold-duotone',
  shelves: 'solar:layers-minimalistic-bold-duotone',
  clients: 'solar:user-id-bold-duotone',
  qr_review: 'solar:qr-code-bold-duotone',
  repairs: 'solar:sledgehammer-bold-duotone',
  warehouse: 'solar:buildings-bold-duotone',
  configurations: 'solar:settings-bold-duotone',
  ai_models: 'solar:cpu-bolt-bold-duotone',
  rejection_reasons: 'solar:danger-bold-duotone',
  labs: 'solar:test-tube-bold-duotone',
  ocr: 'solar:scanner-2-bold-duotone',
  ocr_templates: 'solar:document-text-bold-duotone',
  repair_settings: 'solar:tuning-bold-duotone',
  qa1_ocr: 'solar:shield-check-bold-duotone',
  qa2_ocr: 'solar:shield-plus-bold-duotone',
  qa3_ocr: 'solar:shield-star-bold-duotone',
  localization: 'solar:translation-bold-duotone',
  search: 'solar:magnifer-bold-duotone',
  branding: 'solar:gallery-bold-duotone',
  telescope: 'solar:telescope-bold-duotone',
  dev_tools: 'solar:code-square-bold-duotone'
};

/** Maps action keys to Iconify icon names */
export const ACTION_ICON_MAP: Record<string, string> = {
  view: 'solar:eye-bold',
  create: 'solar:add-circle-bold',
  edit: 'solar:pen-2-bold',
  delete: 'solar:trash-bin-trash-bold',
  force_delete: 'solar:close-circle-bold',
  restore: 'solar:restart-circle-bold',
  export: 'solar:export-bold',
  import: 'solar:import-bold',
  update: 'solar:refresh-bold',
  operate: 'solar:play-bold',
  view_filter: 'solar:filter-bold',
  rollback: 'solar:undo-left-bold',
  recycle: 'solar:restart-bold',
  sync: 'solar:refresh-circle-bold',
  receive_rejection_alerts: 'solar:bell-bing-bold',
  cross_client: 'solar:global-bold',
  view_logo: 'solar:gallery-bold',
  use: 'solar:play-bold',
  release: 'solar:export-bold',
  transfer: 'solar:transfer-horizontal-bold',
  assign: 'solar:user-plus-bold',
  list: 'solar:list-check-bold'
};

/**
 * True when the permission belongs to a group hidden from the Roles UI
 * (e.g. a feature-flagged group that's disabled). The hidden set is
 * computed at runtime by the consuming component — see
 * {@link useHiddenPermissionGroups} — so feature flags can flip it on
 * and off without a re-seed. The backend keeps seeding hidden groups so
 * roles that already hold them aren't stripped on save.
 */
export function isHiddenPermission(
  permName: string,
  hiddenGroups: ReadonlySet<string>
): boolean {
  const group = permName.split('.')[0] ?? '';
  return hiddenGroups.has(group);
}

/** Second-segment markers that identify column-visibility permissions. */
const COLUMN_PERMISSION_SEGMENTS = ['column', 'dynamic_column'] as const;

/**
 * True when the permission name follows `{group}.column.{field}` OR
 * `{group}.dynamic_column.{field_id}`. Both render in the same Column
 * Visibility section of the role permissions panel.
 */
export function isColumnPermission(permName: string): boolean {
  const parts = permName.split('.');
  return (
    parts.length >= 3 &&
    (COLUMN_PERMISSION_SEGMENTS as readonly string[]).includes(parts[1] ?? '')
  );
}

/** True when the permission gates a dynamic-form-driven column. */
export function isDynamicColumnPermission(permName: string): boolean {
  return permName.split('.')[1] === 'dynamic_column';
}

/**
 * Returns the column field name for a column-visibility permission. For
 * static columns this is the field key; for dynamic columns it's the
 * stringified DynamicFormField id.
 */
export function getColumnField(permName: string): string {
  return permName.split('.').slice(2).join('.');
}

/**
 * Extracts the group prefix from a column-visibility permission name.
 * `'users.column.email'` → `'users'`.
 */
export function getColumnGroup(permName: string): string {
  return permName.split('.')[0] ?? '';
}

/**
 * Maps a permission group to the i18n namespace that owns the actual table
 * column header translations. The View Permissions dialog and the side
 * panel look up `{namespace}.{camelField}` first so labels match the table
 * headers verbatim, then fall back to `roles.columns.{field}` and finally
 * to the raw field name.
 */
export const GROUP_FEATURE_NAMESPACE: Record<string, string> = {
  box_types: 'boxTypes',
  boxes: 'packing',
  box_containers: 'packing',
  clients: 'clients',
  container_types: 'containerTypes',
  devices: 'devices',
  handover_scanning: 'handoverScanning',
  ocr: 'ocr',
  ocr_templates: 'ocrTemplates',
  repairs: 'repairs',
  qa1_ocr: 'ocrQa',
  qa2_ocr: 'ocrQa',
  qa3_ocr: 'ocrQa',
  page_indexes: 'sticking',
  photo_scanning: 'photoScanning',
  qr_review: 'qrReview',
  reception: 'reception',
  registrations: 'registration',
  roles: 'roles',
  shelves: 'shelves',
  shipments: 'shipping',
  sorting: 'sorting',
  system_logs: 'systemLogs',
  users: 'users',
  localization: 'localization'
};

/** snake_case → camelCase. */
export function snakeToCamel(s: string): string {
  return s.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
}

/**
 * Sidebar-driven categories for the View Permissions dialog.
 *
 * The categories MUST mirror the application sidebar (`src/config/nav-config.ts`)
 * exactly — no creativity, no manual mapping. If a sidebar item is moved
 * between groups in nav-config, the View Permissions dialog reflects it
 * automatically. This keeps "where do I find the role permissions for X"
 * answerable by looking at the sidebar.
 */
export interface PermissionCategory {
  /** Nav group key from `NAV_GROUP_ORDER` in nav-config. */
  id: string;
  /** Translation key (already namespaced — e.g. `navigation.sidebar.externalLab`). */
  i18nKey: string;
  /** Permission groups derived from sidebar items in this nav group. */
  groups: readonly string[];
}

/**
 * Manual category overrides for permission groups.
 *
 * Use this when:
 *  - a group has no sidebar nav item (would otherwise land in `_uncategorized`), or
 *  - a group is referenced by nav items in multiple categories and you want to
 *    pin it to one (overrides win — the group is removed from any other
 *    category derived from nav).
 */
export const GROUP_CATEGORY_OVERRIDES: Record<string, NavGroup> = {
  ocr: 'internalLab',
  // print_batches is rendered as a nested child of `page_indexes` (see
  // CHILD_GROUP_MAP), but it still needs a category so its permissions are
  // counted in the External Lab totals.
  print_batches: 'externalLab',
  // box_containers nests under `boxes` (Packing) via CHILD_GROUP_MAP.
  box_containers: 'externalLab',
  ocr_templates: 'administration',
  sub_container_types: 'systemSettings',
  // System-wide search has no sidebar nav item — it's mounted in the
  // topbar — so pin it to administration so it doesn't fall into _uncategorized.
  search: 'administration',
  // Logo display gate has no sidebar nav item — it's a system-wide branding
  // toggle — so pin it to systemSettings instead of _uncategorized.
  branding: 'systemSettings',
  // Laravel Telescope debugging dashboard access has no sidebar nav item —
  // the dashboard is fronted by TelescopeAccess middleware — so pin the
  // assignable `telescope.view` permission to systemSettings.
  telescope: 'systemSettings',
  // Developer utilities — pinned to systemSettings so the Roles UI surfaces
  // them in a sensible category rather than _uncategorized.
  dev_tools: 'systemSettings'
};

/**
 * Permission groups rendered as a **nested sub-section inside another
 * group** in the role permissions panel, rather than as their own top-level
 * block. Key = child group, value = parent group.
 *
 * Use this when one group is functionally part of another's workflow and
 * showing it standalone is confusing. Example: `print_batches` gates the
 * "print labels" flow inside Sticking — it's never reached outside
 * `/dashboard/sticking`, so it lives visually under `page_indexes`.
 *
 * Children stay seeded server-side and are still counted toward the parent
 * category's totals; this is purely a UI grouping.
 */
export const CHILD_GROUP_MAP: Record<string, string> = {
  print_batches: 'page_indexes',
  // Containers-inside-box table lives on the box detail page (under
  // /dashboard/packing/{id}). Roles UI nests it under Packing so admins
  // can find the column toggles where the workflow lives.
  box_containers: 'boxes'
};

/**
 * Derives categories from the sidebar nav config. Each nav item's
 * `access.requiredPermissions[0]` (e.g. `'registrations.view'`) gives the
 * permission group prefix; nav items grouped under the same `group` end up
 * in the same View-Permissions category. `GROUP_CATEGORY_OVERRIDES` fills in
 * groups that have no matching nav item.
 *
 * Pure function over a static module-scope array, so the result is stable
 * across renders — no need for memoization at the call site.
 */
export function buildPermissionCategoriesFromNav(): PermissionCategory[] {
  const map = new Map<string, Set<string>>();

  for (const navGroup of NAV_GROUP_ORDER) {
    map.set(navGroup, new Set<string>());
  }

  for (const item of navItems) {
    const navGroup = item.group;
    if (!navGroup || !map.has(navGroup)) continue;

    const perm = item.access?.requiredPermissions?.[0];
    if (!perm) continue;

    const permGroup = perm.split('.')[0];
    if (!permGroup) continue;

    map.get(navGroup)!.add(permGroup);
  }

  // Overrides are authoritative — strip the group from any other category
  // it may have been added to via the nav loop above, then pin it to the
  // target. Without this step, groups referenced by nav items in multiple
  // categories would appear in whichever category comes first in
  // NAV_GROUP_ORDER, ignoring the override.
  for (const [permGroup, navGroup] of Object.entries(
    GROUP_CATEGORY_OVERRIDES
  )) {
    for (const set of map.values()) set.delete(permGroup);
    map.get(navGroup)?.add(permGroup);
  }

  return NAV_GROUP_ORDER.map((id) => ({
    id,
    i18nKey: NAV_GROUP_LABELS[id],
    groups: Array.from(map.get(id) ?? new Set<string>())
  }));
}

/** Cached snapshot — `navItems` is a static module-scope array. */
export const PERMISSION_CATEGORIES: readonly PermissionCategory[] =
  buildPermissionCategoriesFromNav();

/** Reverse lookup: permission group → category id (or `null` if uncategorized). */
export function getGroupCategory(group: string): string | null {
  for (const cat of PERMISSION_CATEGORIES) {
    if (cat.groups.includes(group)) return cat.id;
  }
  return null;
}

/** The action segment of a permission name. `'boxes.self_assign'` → `'self_assign'`. */
export function getPermissionAction(permName: string): string {
  return permName.split('.')[1] ?? permName;
}

/**
 * Human-readable label for an action verb that has no `ACTION_KEY_MAP`
 * entry yet. Turns `'receive_rejection_alerts'` into
 * `'Receive rejection alerts'` so a permission seeded ahead of its
 * translation renders as words instead of a raw `roles.action_x` key.
 */
export function humanizeAction(action: string): string {
  const words = action.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Resolves an action verb to its translated label, falling back to a
 * humanized form. Both permission surfaces call this so a newly seeded
 * verb can never leak a translation key into the UI — the failure that
 * put a bare `assign` chip next to a `roles.action_assign` table header.
 */
export function translateAction(
  action: string,
  t: (key: string) => string
): string {
  const key = ACTION_KEY_MAP[action];
  return key ? t(key) : humanizeAction(action);
}
